import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FaThLarge, FaLayerGroup, FaFileInvoiceDollar, FaHeadset,
    FaSignOutAlt, FaCheck, FaCode,
    FaEnvelope, FaPhone, FaWhatsapp, FaBoxOpen, FaRupeeSign, FaDownload,
    FaUserEdit, FaTimes, FaEye, FaEyeSlash, FaBars,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';
import './ClientDashboard.css';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

const DURATION_OPTIONS = [
    { months: 1,  label: '1 Month',   discount: 0  },
    { months: 3,  label: '3 Months',  discount: 10 },
    { months: 6,  label: '6 Months',  discount: 15 },
    { months: 12, label: '1 Year',    discount: 20 },
];

const calcAmount = (baseMonthly, months) => {
    const opt     = DURATION_OPTIONS.find(o => o.months === months) || DURATION_OPTIONS[0];
    const full    = baseMonthly * months;
    const saving  = Math.round(full * opt.discount / 100);
    const base    = full - saving;
    const gst     = Math.round(base * 0.18);
    const total   = base + gst;
    return { full, saving, base, gst, total, discount: opt.discount };
};

const COMMON_FEATURES = [
    'Fully Custom Website for Your Business',
    'Custom Admin Panel Included',
    'Hosting Included',
    'SSL Security Included',
    'Mobile Responsive Design',
    'WhatsApp Integration',
    'Contact & Inquiry Forms',
    'SEO-Friendly Structure',
    'Fast Loading Website',
    'Regular Maintenance',
    'Lifetime Free Technical Support',
    'Bug Fixes & Security Updates',
];

const PLANS = [
    {
        key: '.in Plan',
        icon: '🌐',
        name: '.in Domain Plan',
        price: '₹799',
        period: '/month',
        tagline: 'Perfect for Indian businesses',
        popular: false,
        domainNote: '.in Domain Included',
        features: COMMON_FEATURES,
    },
    {
        key: '.com Plan',
        icon: '🚀',
        name: '.com Domain Plan',
        price: '₹999',
        period: '/month',
        tagline: 'Global reach with .com domain',
        popular: true,
        domainNote: '.com Domain Included',
        features: COMMON_FEATURES,
    },
    {
        key: 'Custom Plan',
        icon: '⚙️',
        name: 'Custom Domain Plan',
        price: '₹899',
        period: '/month',
        tagline: 'Use your existing or preferred domain',
        popular: false,
        domainNote: 'Your Existing / Custom Domain',
        features: COMMON_FEATURES,
    },
];

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

/* ── Protected wrapper ── */
const ClientProtected = ({ children }) => {
    const navigate = useNavigate();
    useEffect(() => {
        if (!localStorage.getItem('client_token')) navigate('/client-login');
    }, [navigate]);
    return children;
};

const ClientDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const tabFromPath = location.pathname.split('/client-dashboard/')[1] || 'overview';
    const activeTab = ['overview', 'plans', 'invoices', 'poc'].includes(tabFromPath) ? tabFromPath : 'overview';
    const setActiveTab = (tab) => {
        navigate(`/client-dashboard/${tab}`, { replace: true });
        setMobileMenuOpen(false);
    };
    const [profile, setProfile] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [poc, setPoc] = useState([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingPoc, setLoadingPoc] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [planSaving] = useState('');
    // Feature requests
    const [featureRequests, setFeatureRequests] = useState([]);
    const [showFeatureForm, setShowFeatureForm] = useState(false);
    const [featureTitle, setFeatureTitle] = useState('');
    const [featureDesc, setFeatureDesc] = useState('');
    const [submittingFeature, setSubmittingFeature] = useState(false);
    const [selectingPlan, setSelectingPlan] = useState(null);
    const [planDuration, setPlanDuration] = useState(1);
    // Payment flow
    const [paymentModal, setPaymentModal] = useState(null); // { plan, duration, total, base, gst }
    const [paymentMethod, setPaymentMethod] = useState('manual');
    const [paymentQr, setPaymentQr] = useState(null);
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [submittingPayment, setSubmittingPayment] = useState(false);
    const [paymentSubmitted, setPaymentSubmitted] = useState(false);
    const [qrZoomed, setQrZoomed] = useState(false);

    // Edit Profile
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [editForm, setEditForm] = useState({ first_name: '', last_name: '', mobile: '', address: '' });
    const [editPassword, setEditPassword] = useState({ current: '', new_password: '', confirm: '' });
    const [showCurrPass, setShowCurrPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);

    // KYC Re-upload States
    const [reuploadDocType, setReuploadDocType] = useState('');
    const [reuploadFile, setReuploadFile] = useState(null);
    const [reuploadFileName, setReuploadFileName] = useState('');
    const [submittingReupload, setSubmittingReupload] = useState(false);
    const [reuploadError, setReuploadError] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const token = localStorage.getItem('client_token');
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' };

    const handleKycReupload = async (e) => {
        e.preventDefault();
        if (!reuploadDocType) {
            toast.error('Please select a document type.');
            return;
        }
        if (!reuploadFile) {
            toast.error('Please upload your KYC document.');
            return;
        }
        setSubmittingReupload(true);
        setReuploadError('');

        const form = new FormData();
        form.append('kyc_document_type', reuploadDocType);
        form.append('kyc_document', reuploadFile);

        try {
            const res = await fetch(`${API}/client/reupload-kyc`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                },
                body: form
            });
            const text = await res.text();
            const i = text.search(/\{|\[/);
            const json = JSON.parse(i !== -1 ? text.substring(i) : text);

            if (res.ok && json.status) {
                toast.success('KYC document submitted successfully!');
                setProfile(json.data);
                setReuploadDocType('');
                setReuploadFile(null);
                setReuploadFileName('');
            } else {
                setReuploadError(json.message || 'Failed to submit KYC document. Please try again.');
            }
        } catch {
            setReuploadError('Something went wrong. Please check your connection and try again.');
        } finally {
            setSubmittingReupload(false);
        }
    };

    /* ── Seed from login router state immediately, then refresh from API ── */
    useEffect(() => {
        if (location.state?.client) {
            setProfile(location.state.client);
            setLoadingProfile(false);
        }
        fetchProfile();
        fetchInvoices(); // always load invoices so overview stats work
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Fetch tab data on tab switch ── */
    useEffect(() => {
        // invoices already fetched on mount; only re-fetch if explicitly navigated and empty
        if (activeTab === 'invoices' && invoices.length === 0 && !loadingInvoices) fetchInvoices();
        if (activeTab === 'poc' && poc.length === 0) fetchPoc();
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (profile) fetchFeatureRequests();
    }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchProfile = async () => {
        try {
            const res = await fetch(`${API}/client/profile`, { headers });
            if (res.status === 401) {
                localStorage.removeItem('client_token');
                navigate('/client-login');
                return;
            }
            const text = await res.text();
            const jsonStart = text.search(/\{|\[/);
            const json = JSON.parse(jsonStart !== -1 ? text.substring(jsonStart) : text);
            if (json.status) setProfile(json.data);
        } catch {
            // Network/server error — keep token, show error state
        } finally {
            setLoadingProfile(false);
        }
    };

    const parseResponse = async (res) => {
        const text = await res.text();
        const jsonStart = text.search(/\{|\[/);
        return JSON.parse(jsonStart !== -1 ? text.substring(jsonStart) : text);
    };

    const openEditProfile = () => {
        setEditForm({
            first_name: profile?.first_name || '',
            last_name:  profile?.last_name  || '',
            mobile:     profile?.mobile     || '',
            address:    profile?.address    || '',
        });
        setEditPassword({ current: '', new_password: '', confirm: '' });
        setShowEditProfile(true);
    };

    const handleSaveProfile = async () => {
        if (editPassword.new_password && editPassword.new_password !== editPassword.confirm) {
            toast.error('New passwords do not match'); return;
        }
        if (editPassword.new_password && !editPassword.current) {
            toast.error('Enter your current password to change it'); return;
        }
        setSavingProfile(true);
        try {
            const body = { ...editForm };
            if (editPassword.new_password) {
                body.current_password = editPassword.current;
                body.new_password     = editPassword.new_password;
            }
            const res = await fetch(`${API}/client/profile`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await parseResponse(res);
            if (json.status) {
                setProfile(prev => ({ ...prev, ...json.data }));
                setShowEditProfile(false);
                toast.success('Profile updated successfully!');
            } else {
                toast.error(json.message || 'Failed to update profile');
            }
        } catch { toast.error('Network error'); }
        finally { setSavingProfile(false); }
    };

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const res = await fetch(`${API}/client/invoices`, { headers });
            const json = await parseResponse(res);
            if (json.status) setInvoices(json.data || []);
        } catch {}
        finally { setLoadingInvoices(false); }
    };

    const fetchPoc = async () => {
        setLoadingPoc(true);
        try {
            const res = await fetch(`${API}/client/poc`, { headers });
            const json = await parseResponse(res);
            if (json.status) setPoc(json.data || []);
        } catch {}
        finally { setLoadingPoc(false); }
    };

    const downloadInvoice = (inv) => {
        const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

        const itemRows = (inv.items || []).map(item => `
            <tr>
                <td>${item.item_name || '—'}</td>
                <td>${item.description || ''}</td>
                <td style="text-align:center">${item.quantity}</td>
                <td style="text-align:right">${fmt(item.unit_price)}</td>
                <td style="text-align:right">${fmt(item.total_price)}</td>
            </tr>`).join('');

        const statusColor = { paid: '#16a34a', unpaid: '#dc2626', partial: '#c2410c' }[inv.payment_status] || '#64748b';

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Invoice ${inv.invoice_number}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; padding: 40px; font-size: 14px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 3px solid #FF9F1C; padding-bottom: 24px; }
  .brand { display: flex; flex-direction: column; gap: 4px; }
  .brand-name { font-size: 24px; font-weight: 900; color: #0e1629; }
  .brand-tag { font-size: 11px; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; }
  .invoice-meta { text-align: right; }
  .invoice-number { font-size: 20px; font-weight: 900; color: #FF9F1C; }
  .invoice-date { font-size: 12px; color: #64748b; margin-top: 4px; }
  .status-pill { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 11px; font-weight: 800; text-transform: uppercase; background: ${statusColor}22; color: ${statusColor}; margin-top: 6px; }
  .section { display: flex; gap: 40px; margin-bottom: 36px; }
  .block { flex: 1; }
  .block-label { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 8px; }
  .block-value { font-size: 14px; color: #1e293b; line-height: 1.6; }
  .block-value strong { font-weight: 800; font-size: 15px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
  thead tr { background: #0e1629; color: #fff; }
  thead th { padding: 10px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; text-align: left; }
  thead th:nth-child(3) { text-align: center; }
  thead th:nth-child(4), thead th:nth-child(5) { text-align: right; }
  tbody tr { border-bottom: 1px solid #f1f5f9; }
  tbody tr:nth-child(even) { background: #f8faff; }
  tbody td { padding: 10px 14px; font-size: 13px; }
  .totals { margin-top: 24px; display: flex; justify-content: flex-end; }
  .totals-table { width: 280px; }
  .totals-table td { padding: 6px 0; font-size: 13px; }
  .totals-table td:last-child { text-align: right; font-weight: 700; }
  .totals-table tr.grand td { font-size: 16px; font-weight: 900; color: #FF9F1C; border-top: 2px solid #FF9F1C; padding-top: 10px; }
  .footer { margin-top: 48px; border-top: 1px solid #e2e8f0; padding-top: 20px; font-size: 12px; color: #94a3b8; text-align: center; }
  @media print {
    body { padding: 20px; }
    @page { margin: 1cm; }
  }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <div class="brand-name">Nimai Solutions</div>
      <div class="brand-tag">Website · Apps · CRM · Software</div>
      <div style="font-size:12px;color:#64748b;margin-top:6px">+91 70465 48990</div>
    </div>
    <div class="invoice-meta">
      <div class="invoice-number">INVOICE</div>
      <div style="font-size:22px;font-weight:900;color:#0e1629;">${inv.invoice_number}</div>
      <div class="invoice-date">Date: ${fmtDate(inv.invoice_date)}</div>
      <div class="invoice-date">Due: ${fmtDate(inv.due_date)}</div>
      <div class="status-pill">${(inv.payment_status || '').toUpperCase()}</div>
    </div>
  </div>

  <div class="section">
    <div class="block">
      <div class="block-label">Billed To</div>
      <div class="block-value">
        <strong>${inv.customer_name || '—'}</strong><br/>
        ${inv.customer_email || ''}<br/>
        ${inv.customer_phone || ''}<br/>
        ${inv.customer_address || ''}
      </div>
    </div>
    <div class="block">
      <div class="block-label">From</div>
      <div class="block-value">
        <strong>Nimai Solutions</strong><br/>
        Vadodara, Gujarat, India<br/>
        +91 70465 48990
      </div>
    </div>
  </div>

  <table>
    <thead><tr>
      <th>Item</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th>
    </tr></thead>
    <tbody>${itemRows || `<tr><td colspan="5" style="text-align:center;padding:20px;color:#94a3b8">No items</td></tr>`}</tbody>
  </table>

  <div class="totals">
    <table class="totals-table">
      <tr><td>Subtotal</td><td>${fmt(inv.subtotal)}</td></tr>
      ${inv.discount > 0 ? `<tr><td>Discount</td><td>− ${fmt(inv.discount)}</td></tr>` : ''}
      <tr class="grand"><td>Total</td><td>${fmt(inv.total_amount)}</td></tr>
      <tr><td style="font-size:12px;color:#94a3b8">Amount Paid</td><td style="font-size:12px;color:#16a34a">${fmt(inv.paid_amount)}</td></tr>
    </table>
  </div>

  <div class="footer">
    Thank you for your business! For support, contact us at +91 70465 48990<br/>
    Nimai Solutions · Website Development · Mobile Apps · CRM Solutions · Custom Software
  </div>

  <script>window.onload = () => { window.print(); }</script>
</body>
</html>`;

        const win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
    };

    const fetchFeatureRequests = async () => {
        try {
            const res = await fetch(`${API}/client/feature-requests`, { headers });
            const json = await parseResponse(res);
            if (json.status) setFeatureRequests(json.data || []);
        } catch {}
    };

    const handleSubmitFeature = async () => {
        if (!featureTitle.trim()) return;
        setSubmittingFeature(true);
        try {
            const res = await fetch(`${API}/client/request-feature`, {
                method: 'POST', headers,
                body: JSON.stringify({ title: featureTitle, description: featureDesc }),
            });
            const json = await parseResponse(res);
            if (json.status) {
                setFeatureRequests(prev => [json.data, ...prev]);
                setFeatureTitle(''); setFeatureDesc(''); setShowFeatureForm(false);
            }
        } catch {}
        finally { setSubmittingFeature(false); }
    };

    const openPaymentModal = async (planKey, duration) => {
        // Fetch payment method + QR before showing modal
        try {
            const res = await fetch(`${API}/payment-method`);
            const text = await res.text();
            const i = text.search(/\{|\[/);
            const json = JSON.parse(i !== -1 ? text.substring(i) : text);
            setPaymentMethod(json.method || 'manual');
            setPaymentQr(json.qr_code || null);
        } catch {}

        const prices = { '.in Plan': 799, '.com Plan': 999, 'Custom Plan': 899 };
        const c = calcAmount(prices[planKey], duration);
        setPaymentModal({ plan: planKey, duration, ...c });
        setSelectingPlan(null);
        setScreenshot(null);
        setScreenshotPreview(null);
        setPaymentSubmitted(false);
    };

    const handleSubmitPayment = async () => {
        if (!screenshot) return;
        setSubmittingPayment(true);
        try {
            const form = new FormData();
            form.append('plan', paymentModal.plan);
            form.append('duration', paymentModal.duration);
            form.append('screenshot', screenshot);
            const res = await fetch(`${API}/client/submit-plan-payment`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                body: form,
            });
            const text = await res.text();
            const i = text.search(/\{|\[/);
            const json = JSON.parse(i !== -1 ? text.substring(i) : text);
            if (json.status) {
                setPaymentSubmitted(true);
            }
        } catch {}
        finally { setSubmittingPayment(false); }
    };

    const handleLogout = () => {
        localStorage.removeItem('client_token');
        navigate('/client-login');
    };

    if (loadingProfile) {
        return (
            <div className="cd-layout">
                <aside className="cd-sidebar">
                    <div className="cd-brand">
                        <img src={logo} alt="Nimai Solutions" />
                        <span>Client Portal</span>
                    </div>
                    <nav className="cd-nav">
                        {[1,2,3,4].map(i => (
                            <div key={i} className="cd-skel cd-skel-nav" />
                        ))}
                    </nav>
                </aside>
                <div className="cd-main">
                    <div className="cd-topbar">
                        <div className="cd-skel cd-skel-title" />
                        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                            <div>
                                <div className="cd-skel cd-skel-name" />
                                <div className="cd-skel cd-skel-sub" style={{ marginTop:5 }} />
                            </div>
                            <div className="cd-skel cd-skel-avatar" />
                        </div>
                    </div>
                    <div className="cd-content">
                        {/* Overview skeleton */}
                        <div className="cd-skeleton-wrap">
                            <div className="cd-skel cd-skel-card-full" style={{ marginBottom:20 }} />
                            <div className="cd-skel-row">
                                {[1,2,3,4].map(i => <div key={i} className="cd-skel cd-skel-stat" />)}
                            </div>
                            <div className="cd-skel cd-skel-card-full" style={{ height:160, marginTop:20 }} />
                            <div className="cd-skel cd-skel-card-full" style={{ height:120, marginTop:16 }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const initials = profile
        ? `${profile.first_name?.charAt(0) || ''}${profile.last_name?.charAt(0) || ''}`
        : '?';

    const totalPaid = invoices.filter(i => i.payment_status === 'paid').reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

    const isKycApproved = profile?.kyc_status === 'approved';

    const renderKycWall = () => {
        const docTypes = ['Aadhaar Card', 'PAN Card', 'Passport', 'Driving License', 'Voter ID'];
        const isRejected = profile?.kyc_status === 'rejected';
        const isPending = profile?.kyc_status === 'pending';

        return (
            <div className="kyc-wall-wrapper">
                <div className="kyc-wall-card">
                    <div className="kyc-wall-brand">
                        <img src={logo} alt="Nimai Solutions" />
                        <span>Nimai Solutions</span>
                    </div>

                    <h1 className="kyc-wall-title">KYC Verification</h1>
                    <p className="kyc-wall-subtitle">
                        To activate your account and access dashboard features, we require a one-time KYC verification.
                    </p>

                    {isPending && (
                        <div className="kyc-wall-alert pending">
                            <div className="kyc-alert-icon">⏳</div>
                            <div className="kyc-alert-content">
                                <h3>Verification Pending Review</h3>
                                <p>
                                    Your documents have been submitted and are currently awaiting review by our administrators. 
                                    We will notify you once the verification is completed.
                                </p>
                            </div>
                        </div>
                    )}

                    {isRejected && (
                        <div className="kyc-wall-alert rejected">
                            <div className="kyc-alert-icon">❌</div>
                            <div className="kyc-alert-content">
                                <h3>Verification Rejected</h3>
                                <p>
                                    Unfortunately, your document verification could not be completed.
                                </p>
                                {profile.kyc_reject_reason && (
                                    <div className="kyc-reject-reason-box">
                                        <strong>Reason: </strong> {profile.kyc_reject_reason}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isPending && profile.kyc_document_type && (
                        <div className="kyc-submitted-details">
                            <strong>Submitted Document:</strong> {profile.kyc_document_type}
                        </div>
                    )}

                    {(isRejected || (!isKycApproved && !isPending)) && (
                        <form onSubmit={handleKycReupload} className="kyc-reupload-form" noValidate>
                            <div className="kyc-form-field">
                                <label>Document Type</label>
                                <select
                                    value={reuploadDocType}
                                    onChange={e => setReuploadDocType(e.target.value)}
                                    required
                                >
                                    <option value="">Select document type...</option>
                                    {docTypes.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="kyc-form-field">
                                <label>Upload KYC Document</label>
                                <label className="kyc-upload-input-label">
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setReuploadFile(file);
                                                setReuploadFileName(file.name);
                                            }
                                        }}
                                        required
                                    />
                                    <span className="kyc-upload-browse-btn">
                                        {reuploadFileName ? reuploadFileName : 'Choose file (JPG, PNG, PDF)'}
                                    </span>
                                </label>
                            </div>

                            {reuploadError && <p className="kyc-form-error">{reuploadError}</p>}

                            <button
                                type="submit"
                                className="kyc-submit-btn"
                                disabled={submittingReupload || !reuploadDocType || !reuploadFile}
                            >
                                {submittingReupload ? 'Submitting...' : 'Submit Documents'}
                            </button>
                        </form>
                    )}

                    <div className="kyc-wall-footer">
                        <button type="button" className="kyc-logout-btn" onClick={handleLogout}>
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const NAV = [
        { key: 'overview', label: 'Overview',  icon: <FaThLarge /> },
        { key: 'plans',    label: 'Plans',      icon: <FaLayerGroup /> },
        { key: 'invoices', label: 'Invoices',   icon: <FaFileInvoiceDollar /> },
        { key: 'poc',      label: 'Point of Contact', icon: <FaHeadset /> },
    ];

    return (
        <ClientProtected>
            {!isKycApproved ? (
                renderKycWall()
            ) : (
                <div className="cd-layout">

                {/* ── Sidebar ── */}
                <aside className={`cd-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
                    <div className="cd-brand">
                        <img src={logo} alt="Nimai Solutions" />
                        <span>Client Portal</span>
                        <button className="cd-sidebar-close" onClick={() => setMobileMenuOpen(false)}>
                            <FaTimes />
                        </button>
                    </div>
                    <nav className="cd-nav">
                        {NAV.map(n => (
                            <button
                                key={n.key}
                                className={`cd-nav-item ${activeTab === n.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(n.key)}
                            >
                                {n.icon} {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="cd-sidebar-footer">
                        <button className="cd-logout-btn" onClick={handleLogout}>
                            <FaSignOutAlt /> Logout
                        </button>
                    </div>
                </aside>

                {/* Mobile Backdrop Overlay */}
                {mobileMenuOpen && (
                    <div className="cd-sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />
                )}

                {/* ── Main ── */}
                <div className="cd-main">

                    {/* Topbar */}
                    <div className="cd-topbar">
                        <button className="cd-hamburger-btn" onClick={() => setMobileMenuOpen(true)}>
                            <FaBars />
                        </button>
                        <span className="cd-topbar-title">{NAV.find(n => n.key === activeTab)?.label}</span>
                        <div className="cd-topbar-client">
                            <div className="cd-client-info">
                                <div className="cd-client-name">{profile?.first_name} {profile?.last_name}</div>
                                <div className="cd-client-project">{profile?.project_name}</div>
                            </div>
                            <button className="cd-edit-profile-btn" onClick={openEditProfile} title="Edit Profile">
                                <FaUserEdit />
                            </button>
                            <div className="cd-client-avatar">{initials}</div>
                        </div>
                    </div>

                    <div className="cd-content">

                        {/* ════════ OVERVIEW ════════ */}
                        {activeTab === 'overview' && (() => {
                            // Plan end date calculation
                            const planEnd = profile?.plan_start_date && profile?.plan_duration
                                ? (() => {
                                    const d = new Date(profile.plan_start_date);
                                    d.setMonth(d.getMonth() + parseInt(profile.plan_duration));
                                    return d;
                                })()
                                : null;
                            const daysLeft = planEnd
                                ? Math.ceil((planEnd - new Date()) / (1000 * 60 * 60 * 24))
                                : null;
                            const planExpired = daysLeft !== null && daysLeft <= 0;
                            const planSoon    = daysLeft !== null && daysLeft > 0 && daysLeft <= 15;

                            const frStatus = { pending: { label: 'Pending', cls: 'fr-pending' }, in_progress: { label: 'In Progress', cls: 'fr-progress' }, completed: { label: 'Done ✓', cls: 'fr-done' }, rejected: { label: 'Rejected', cls: 'fr-rejected' } };

                            return (
                            <>
                                {/* Project card */}
                                <div className="cd-project-card">
                                    {profile?.project_logo ? (
                                        <img className="cd-project-logo" src={`https://aayushrealproperties.in/nimaibackend/public/storage/${profile.project_logo}`} alt="Project Logo" />
                                    ) : (
                                        <div className="cd-project-logo-placeholder"><FaCode /></div>
                                    )}
                                    <div className="cd-project-info">
                                        <h3>{profile?.project_name}</h3>
                                        <p>{profile?.project_type} · {profile?.email}</p>
                                        <span className={`cd-plan-pill ${!profile?.selected_plan ? 'none' : ''}`}>
                                            {profile?.selected_plan ? `📦 ${profile.selected_plan}` : 'No plan selected yet'}
                                        </span>
                                    </div>
                                </div>

                                {/* Website & Admin Links */}
                                {(profile?.website_url || profile?.admin_panel_url) && (
                                    <div className="cd-links-row">
                                        {profile?.website_url && (
                                            <a href={profile.website_url} target="_blank" rel="noreferrer" className="cd-link-card">
                                                <div className="cd-link-icon">🌐</div>
                                                <div className="cd-link-info">
                                                    <div className="cd-link-label">Your Website</div>
                                                    <div className="cd-link-url">{profile.website_url}</div>
                                                </div>
                                                <span className="cd-link-arrow">↗</span>
                                            </a>
                                        )}
                                        {profile?.admin_panel_url && (
                                            <a href={profile.admin_panel_url} target="_blank" rel="noreferrer" className="cd-link-card">
                                                <div className="cd-link-icon">⚙️</div>
                                                <div className="cd-link-info">
                                                    <div className="cd-link-label">Admin Panel</div>
                                                    <div className="cd-link-url">{profile.admin_panel_url}</div>
                                                </div>
                                                <span className="cd-link-arrow">↗</span>
                                            </a>
                                        )}
                                    </div>
                                )}

                                {/* Plan status */}
                                {profile?.selected_plan && (
                                    <div className={`cd-plan-status-card ${planExpired ? 'expired' : planSoon ? 'expiring' : 'active'}`}>
                                        <div className="cd-plan-status-left">
                                            <div className="cd-plan-status-label">
                                                {planExpired ? '🔴 Plan Expired' : planSoon ? '🟡 Expiring Soon' : '🟢 Active Plan'}
                                            </div>
                                            <div className="cd-plan-status-name">{profile.selected_plan} · {profile.plan_duration} month{profile.plan_duration > 1 ? 's' : ''}</div>
                                            <div className="cd-plan-status-dates">
                                                <span>Start: {profile.plan_start_date ? formatDate(profile.plan_start_date) : '—'}</span>
                                                <span className="cd-date-divider">·</span>
                                                <span>Due: {planEnd ? formatDate(planEnd.toISOString()) : '—'}</span>
                                                {daysLeft > 0 && <span className="cd-days-left"> ({daysLeft} days left)</span>}
                                                {planExpired && <span className="cd-days-left expired-text"> (Expired)</span>}
                                            </div>
                                        </div>
                                        <button className="cd-renew-btn" onClick={() => setActiveTab('plans')}>
                                            🔄 Renew Plan
                                        </button>
                                    </div>
                                )}

                                {/* Stats */}
                                <div className="cd-overview-grid">
                                    <div className="cd-stat-card">
                                        <div className="cd-stat-icon orange"><FaLayerGroup /></div>
                                        <div>
                                            <div className="cd-stat-label">Selected Plan</div>
                                            <div className="cd-stat-value" style={{ fontSize: '1rem' }}>{profile?.selected_plan || '—'}</div>
                                        </div>
                                    </div>
                                    <div className="cd-stat-card">
                                        <div className="cd-stat-icon blue"><FaFileInvoiceDollar /></div>
                                        <div>
                                            <div className="cd-stat-label">Total Invoices</div>
                                            <div className="cd-stat-value">{invoices.length || '—'}</div>
                                        </div>
                                    </div>
                                    <div className="cd-stat-card">
                                        <div className="cd-stat-icon green"><FaRupeeSign /></div>
                                        <div>
                                            <div className="cd-stat-label">Total Paid</div>
                                            <div className="cd-stat-value" style={{ fontSize: '1rem' }}>{totalPaid ? formatCurrency(totalPaid) : '—'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Completed Features */}
                                {profile?.features?.length > 0 && (
                                    <div className="cd-section-block">
                                        <div className="cd-section-block-title">✅ Completed Features</div>
                                        <div className="cd-features-grid">
                                            {profile.features.map((f, i) => (
                                                <div key={i} className="cd-feature-item"><FaCheck /> {f}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Feature Requests */}
                                <div className="cd-section-block">
                                    <div className="cd-section-block-header">
                                        <div className="cd-section-block-title">💡 Feature Requests</div>
                                        <button className="cd-req-feature-btn" onClick={() => setShowFeatureForm(p => !p)}>
                                            {showFeatureForm ? 'Cancel' : '+ Request Feature'}
                                        </button>
                                    </div>

                                    {showFeatureForm && (
                                        <div className="cd-feature-form">
                                            <input
                                                className="cd-feature-input"
                                                placeholder="Feature title *"
                                                value={featureTitle}
                                                onChange={e => setFeatureTitle(e.target.value)}
                                            />
                                            <textarea
                                                className="cd-feature-textarea"
                                                placeholder="Describe the feature in detail (optional)"
                                                rows={3}
                                                value={featureDesc}
                                                onChange={e => setFeatureDesc(e.target.value)}
                                            />
                                            <button className="cd-feature-submit" onClick={handleSubmitFeature} disabled={submittingFeature || !featureTitle.trim()}>
                                                {submittingFeature ? 'Submitting…' : 'Submit Request'}
                                            </button>
                                        </div>
                                    )}

                                    {featureRequests.length === 0 ? (
                                        <p className="cd-fr-empty">No feature requests yet. Click "Request Feature" to submit one.</p>
                                    ) : (
                                        <div className="cd-fr-list">
                                            {featureRequests.map(fr => (
                                                <div key={fr.id} className="cd-fr-item">
                                                    <div className="cd-fr-title">{fr.title}</div>
                                                    {fr.description && <div className="cd-fr-desc">{fr.description}</div>}
                                                    <div className="cd-fr-meta">
                                                        <span className={`cd-fr-status ${frStatus[fr.status]?.cls}`}>{frStatus[fr.status]?.label}</span>
                                                        <span className="cd-fr-date">{formatDate(fr.created_at)}</span>
                                                    </div>
                                                    {fr.admin_notes && <div className="cd-fr-notes">💬 {fr.admin_notes}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                            );
                        })()}

                        {/* ════════ PLANS ════════ */}
                        {activeTab === 'plans' && (
                            <>
                                <h2 className="cd-section-title">Website Subscription Plans</h2>
                                <p className="cd-section-sub">No large upfront investment — just a simple monthly payment. We handle development, hosting, maintenance, security, and support.</p>

                                <div className="cd-plans-grid">
                                    {PLANS.map(plan => {
                                        const hasPlan        = !!profile?.selected_plan;
                                        const isCurrentPlan  = profile?.selected_plan === plan.key;
                                        const isLockedOut    = hasPlan && !isCurrentPlan;
                                        const isConfiguring  = selectingPlan === plan.key;
                                        const currentDur     = parseInt(profile?.plan_duration || 0);
                                        const basePrice      = parseInt(plan.price.replace('₹', '').replace(',', ''));
                                        // eslint-disable-next-line no-unused-vars
                                        const calc           = calcAmount(basePrice, isConfiguring ? planDuration : 1);

                                        // For current plan: only allow durations ABOVE current
                                        const upgradeDurations = DURATION_OPTIONS
                                            .filter(o => isCurrentPlan ? o.months > currentDur : true);

                                        const noUpgradeAvailable = isCurrentPlan && upgradeDurations.length === 0;

                                        return (
                                            <div
                                                key={plan.key}
                                                className={`cd-plan-card ${isCurrentPlan ? 'selected' : ''} ${isLockedOut ? 'locked' : ''} ${plan.popular && !isCurrentPlan && !isLockedOut ? 'popular' : ''}`}
                                            >
                                                {isCurrentPlan && <div className="cd-plan-selected-badge">✓ Your Plan</div>}
                                                {isLockedOut   && <div className="cd-plan-locked-badge">🔒 Locked</div>}
                                                {plan.popular && !isCurrentPlan && !isLockedOut && <div className="cd-plan-badge">Most Popular</div>}

                                                <div className="cd-plan-icon">{plan.icon}</div>
                                                <div className="cd-plan-name">{plan.name}</div>
                                                <div className="cd-plan-price">
                                                    <span className="cd-plan-price-amount">{plan.price}</span>
                                                    <span className="cd-plan-price-period">{plan.period}</span>
                                                    <span className="cd-plan-gst">+GST</span>
                                                </div>
                                                <div className="cd-plan-domain-note">{plan.domainNote}</div>
                                                <div className="cd-plan-tagline">{plan.tagline}</div>

                                                <ul className="cd-plan-features">
                                                    {plan.features.map((f, i) => (
                                                        <li key={i}><FaCheck /> {f}</li>
                                                    ))}
                                                </ul>

                                                {/* Duration picker (only shown when configuring) */}
                                                {isConfiguring && (
                                                    <div className="cd-duration-picker">
                                                        <div className="cd-duration-label">
                                                            {isCurrentPlan ? 'Upgrade Duration' : 'Select Duration'}
                                                        </div>
                                                        <div className="cd-duration-options">
                                                            {upgradeDurations.map(opt => {
                                                                const c = calcAmount(basePrice, opt.months);
                                                                return (
                                                                    <button
                                                                        key={opt.months}
                                                                        className={`cd-dur-btn ${planDuration === opt.months ? 'active' : ''}`}
                                                                        onClick={() => setPlanDuration(opt.months)}
                                                                    >
                                                                        {opt.discount > 0 && (
                                                                            <span className="cd-dur-discount-tag">{opt.discount}% OFF</span>
                                                                        )}
                                                                        <span className="cd-dur-btn-months">{opt.months}</span>
                                                                        <span className="cd-dur-btn-label">{opt.months === 12 ? 'Year' : 'Months'}</span>
                                                                        <span className="cd-dur-btn-price">₹{c.total.toLocaleString('en-IN')}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        {(() => {
                                                            const c = calcAmount(basePrice, planDuration);
                                                            return (
                                                                <div className="cd-duration-total-block">
                                                                    {c.discount > 0 && (
                                                                        <div className="cd-dur-savings">
                                                                            🎉 You save ₹{c.saving.toLocaleString('en-IN')} ({c.discount}% off)
                                                                        </div>
                                                                    )}
                                                                    <div className="cd-duration-total">
                                                                        {c.discount > 0 && (
                                                                            <span className="cd-dur-original">₹{c.full.toLocaleString('en-IN')}</span>
                                                                        )}
                                                                        ₹{c.base.toLocaleString('en-IN')} + ₹{c.gst.toLocaleString('en-IN')} GST
                                                                        <span className="cd-dur-months"> = ₹{c.total.toLocaleString('en-IN')} total</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                        <div className="cd-duration-actions">
                                                            <button className="cd-dur-cancel" onClick={() => setSelectingPlan(null)}>Cancel</button>
                                                            <button
                                                                className="cd-dur-confirm"
                                                                onClick={() => openPaymentModal(plan.key, planDuration)}
                                                            >
                                                                Proceed to Payment →
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Bottom button */}
                                                {!isConfiguring && !isLockedOut && (
                                                    noUpgradeAvailable ? (
                                                        <div className="cd-plan-max-note">✅ You're on the maximum duration (1 Year)</div>
                                                    ) : (
                                                        <button
                                                            className={`cd-plan-select-btn ${isCurrentPlan ? 'active' : ''}`}
                                                            onClick={() => {
                                                                setSelectingPlan(plan.key);
                                                                // Default to first available upgrade duration
                                                                setPlanDuration(upgradeDurations[0]?.months || 1);
                                                            }}
                                                        >
                                                            {isCurrentPlan
                                                                ? `⬆ Upgrade (currently ${currentDur} month${currentDur > 1 ? 's' : ''})`
                                                                : 'Select Plan'}
                                                        </button>
                                                    )
                                                )}

                                                {!isConfiguring && isLockedOut && (
                                                    <div className="cd-plan-locked-note">
                                                        You are subscribed to <strong>{profile.selected_plan}</strong>. Plan switching is not available.
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* ════════ INVOICES ════════ */}
                        {activeTab === 'invoices' && (
                            <>
                                <h2 className="cd-section-title">Your Invoices</h2>
                                <p className="cd-section-sub">All invoices raised against your account.</p>

                                {loadingInvoices ? (
                                    <div className="cd-skeleton-wrap">
                                        {[1,2,3].map(i => (
                                            <div key={i} className="cd-skel cd-skel-row-item" style={{ marginBottom:10 }} />
                                        ))}
                                    </div>
                                ) : invoices.length === 0 ? (
                                    <div className="cd-invoices-table-wrap">
                                        <div className="cd-empty">
                                            <FaBoxOpen />
                                            <h3>No invoices yet</h3>
                                            <p>Your invoices will appear here once they are generated.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="cd-invoices-table-wrap">
                                        <table className="cd-invoices-table">
                                            <thead>
                                                <tr>
                                                    <th>Invoice #</th>
                                                    <th>Invoice Date</th>
                                                    <th>Due Date</th>
                                                    <th>Amount</th>
                                                    <th>Paid</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoices.map(inv => (
                                                    <tr key={inv.id}>
                                                        <td><strong>{inv.invoice_number}</strong></td>
                                                        <td>{formatDate(inv.invoice_date)}</td>
                                                        <td>{formatDate(inv.due_date)}</td>
                                                        <td>{formatCurrency(inv.total_amount)}</td>
                                                        <td>{formatCurrency(inv.paid_amount)}</td>
                                                        <td>
                                                            <span className={`inv-status ${inv.payment_status}`}>
                                                                {inv.payment_status.charAt(0).toUpperCase() + inv.payment_status.slice(1)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="inv-download-btn"
                                                                title="Download Invoice"
                                                                onClick={() => downloadInvoice(inv)}
                                                            >
                                                                <FaDownload /> PDF
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ════════ POC ════════ */}
                        {activeTab === 'poc' && (
                            <>
                                <h2 className="cd-section-title">Point of Contact</h2>
                                <p className="cd-section-sub">Your dedicated team members for this project.</p>

                                {loadingPoc ? (
                                    <div className="cd-skeleton-wrap">
                                        {[1,2].map(i => (
                                            <div key={i} className="cd-skel cd-skel-poc" style={{ marginBottom:14 }} />
                                        ))}
                                    </div>
                                ) : poc.length === 0 ? (
                                    <div className="cd-empty">
                                        <FaHeadset />
                                        <h3>No contacts assigned yet</h3>
                                        <p>Your dedicated contacts will appear here once assigned by the team.</p>
                                    </div>
                                ) : (
                                    <div className="cd-poc-grid">
                                        {poc.map(p => (
                                            <div key={p.id} className="cd-poc-card">
                                                <div className="cd-poc-avatar">
                                                    {p.avatar
                                                        ? <img src={`https://aayushrealproperties.in/nimaibackend/public/storage/${p.avatar}`} alt={p.name} />
                                                        : p.name?.charAt(0)
                                                    }
                                                </div>
                                                <div className="cd-poc-name">{p.name}</div>
                                                <div className="cd-poc-role">{p.role}</div>
                                                <div className="cd-poc-contacts">
                                                    {p.email && (
                                                        <div className="cd-poc-contact-row">
                                                            <FaEnvelope /> {p.email}
                                                        </div>
                                                    )}
                                                    {p.phone && (
                                                        <div className="cd-poc-contact-row">
                                                            <FaPhone /> {p.phone}
                                                        </div>
                                                    )}
                                                </div>
                                                {p.whatsapp && (
                                                    <a
                                                        href={`https://wa.me/${p.whatsapp.replace(/\D/g, '')}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="cd-poc-wa-btn"
                                                    >
                                                        <FaWhatsapp /> Chat on WhatsApp
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </div>
            </div>
            )}
                {/* ── QR Zoom Lightbox ── */}
                {qrZoomed && paymentQr && (
                    <div className="cd-qr-lightbox" onClick={() => setQrZoomed(false)}>
                        <div className="cd-qr-lightbox-inner" onClick={e => e.stopPropagation()}>
                            <button className="cd-qr-lightbox-close" onClick={() => setQrZoomed(false)}>✕</button>
                            <img src={paymentQr} alt="Payment QR" />
                            <a href={paymentQr} download="payment-qr.png" className="cd-qr-lightbox-download">
                                ⬇ Download QR Code
                            </a>
                        </div>
                    </div>
                )}

                {/* ── Payment Modal ── */}
                {paymentModal && (
                    <div className="cd-pay-overlay" onClick={() => !paymentSubmitted && setPaymentModal(null)}>
                        <div className="cd-pay-modal" onClick={e => e.stopPropagation()}>
                            {paymentSubmitted ? (
                                <div className="cd-pay-success">
                                    <div className="cd-pay-success-icon">✅</div>
                                    <h3>Payment Submitted!</h3>
                                    <p>Your payment screenshot has been sent for review. Our team will activate your plan within 24 hours.</p>
                                    <button className="cd-dur-confirm" style={{ width: '100%' }} onClick={() => setPaymentModal(null)}>Done</button>
                                </div>
                            ) : (
                                <>
                                    <div className="cd-pay-modal-header">
                                        <h3>Complete Payment</h3>
                                        <button className="cd-pay-close" onClick={() => setPaymentModal(null)}>✕</button>
                                    </div>

                                    <div className="cd-pay-summary">
                                        <div className="cd-pay-row"><span>Plan</span><strong>{paymentModal.plan}</strong></div>
                                        <div className="cd-pay-row"><span>Duration</span><strong>{paymentModal.duration} month{paymentModal.duration > 1 ? 's' : ''}</strong></div>
                                        {paymentModal.discount > 0 && (
                                            <div className="cd-pay-row">
                                                <span>List Price</span>
                                                <strong style={{ textDecoration: 'line-through', color: '#94a3b8' }}>₹{paymentModal.full.toLocaleString('en-IN')}</strong>
                                            </div>
                                        )}
                                        {paymentModal.discount > 0 && (
                                            <div className="cd-pay-row">
                                                <span>Discount ({paymentModal.discount}% off)</span>
                                                <strong style={{ color: '#16a34a' }}>− ₹{paymentModal.saving.toLocaleString('en-IN')}</strong>
                                            </div>
                                        )}
                                        <div className="cd-pay-row"><span>Subtotal</span><strong>₹{paymentModal.base.toLocaleString('en-IN')}</strong></div>
                                        <div className="cd-pay-row"><span>GST (18%)</span><strong>₹{paymentModal.gst.toLocaleString('en-IN')}</strong></div>
                                        <div className="cd-pay-row total"><span>Total Payable</span><strong>₹{paymentModal.total.toLocaleString('en-IN')}</strong></div>
                                    </div>

                                    {paymentMethod === 'manual' ? (
                                        <>
                                            <div className="cd-pay-qr-section">
                                                <p className="cd-pay-qr-label">Scan QR to Pay</p>
                                                {paymentQr ? (
                                                    <>
                                                        <img
                                                            src={paymentQr}
                                                            alt="Payment QR"
                                                            className="cd-pay-qr-img"
                                                            title="Click to enlarge"
                                                            onClick={() => setQrZoomed(true)}
                                                        />
                                                        <div className="cd-pay-qr-actions">
                                                            <button className="cd-pay-qr-btn" onClick={() => setQrZoomed(true)}>
                                                                🔍 View Full Size
                                                            </button>
                                                            <a
                                                                href={paymentQr}
                                                                download="payment-qr.png"
                                                                className="cd-pay-qr-btn"
                                                            >
                                                                ⬇ Download QR
                                                            </a>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="cd-pay-qr-placeholder">QR not set by admin</div>
                                                )}
                                                <p className="cd-pay-qr-hint">Pay ₹{paymentModal.total.toLocaleString('en-IN')} via UPI and upload the payment screenshot below.</p>
                                            </div>

                                            <div className="cd-pay-upload">
                                                <label className="cd-pay-upload-label">
                                                    {screenshotPreview ? (
                                                        <img src={screenshotPreview} alt="Preview" className="cd-pay-ss-preview" />
                                                    ) : (
                                                        <div className="cd-pay-upload-placeholder">
                                                            <FaFileInvoiceDollar />
                                                            <span>Click to upload payment screenshot</span>
                                                            <span className="cd-pay-upload-hint">JPG, PNG (max 5MB)</span>
                                                        </div>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={e => {
                                                            const f = e.target.files[0];
                                                            if (f) { setScreenshot(f); setScreenshotPreview(URL.createObjectURL(f)); }
                                                        }}
                                                    />
                                                </label>
                                            </div>

                                            <button
                                                className="cd-dur-confirm"
                                                style={{ width: '100%', padding: '13px', fontSize: '0.9rem' }}
                                                onClick={handleSubmitPayment}
                                                disabled={!screenshot || submittingPayment}
                                            >
                                                {submittingPayment ? 'Submitting…' : 'Submit Payment for Approval'}
                                            </button>
                                        </>
                                    ) : (
                                        <div className="cd-pay-easebuzz">
                                            <p>Online payment via Easebuzz will open here.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Edit Profile Modal ── */}
                {showEditProfile && (
                    <div className="cd-modal-overlay" onClick={() => setShowEditProfile(false)}>
                        <div className="cd-edit-modal" onClick={e => e.stopPropagation()}>
                            <div className="cd-edit-modal-header">
                                <div className="cd-edit-modal-title">
                                    <FaUserEdit /> Edit Profile
                                </div>
                                <button className="cd-edit-modal-close" onClick={() => setShowEditProfile(false)}>
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="cd-edit-modal-body">
                                {/* Personal Info */}
                                <div className="cd-edit-section-title">Personal Information</div>
                                <div className="cd-edit-row">
                                    <div className="cd-edit-field">
                                        <label>First Name</label>
                                        <input
                                            className="cd-edit-input"
                                            value={editForm.first_name}
                                            onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                                            placeholder="First name"
                                        />
                                    </div>
                                    <div className="cd-edit-field">
                                        <label>Last Name</label>
                                        <input
                                            className="cd-edit-input"
                                            value={editForm.last_name}
                                            onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                                            placeholder="Last name"
                                        />
                                    </div>
                                </div>
                                <div className="cd-edit-field">
                                    <label>Mobile Number</label>
                                    <input
                                        className="cd-edit-input"
                                        value={editForm.mobile}
                                        onChange={e => setEditForm(f => ({ ...f, mobile: e.target.value }))}
                                        placeholder="Mobile number"
                                    />
                                </div>
                                <div className="cd-edit-field">
                                    <label>Address</label>
                                    <textarea
                                        className="cd-edit-input cd-edit-textarea"
                                        value={editForm.address}
                                        onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                                        placeholder="Your address"
                                        rows={3}
                                    />
                                </div>

                                {/* Change Password */}
                                <div className="cd-edit-section-title" style={{ marginTop: 8 }}>Change Password <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#94a3b8' }}>(optional)</span></div>
                                <div className="cd-edit-field">
                                    <label>Current Password</label>
                                    <div className="cd-edit-pass-wrap">
                                        <input
                                            className="cd-edit-input"
                                            type={showCurrPass ? 'text' : 'password'}
                                            value={editPassword.current}
                                            onChange={e => setEditPassword(p => ({ ...p, current: e.target.value }))}
                                            placeholder="Enter current password"
                                        />
                                        <button className="cd-pass-toggle" onClick={() => setShowCurrPass(v => !v)}>
                                            {showCurrPass ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>
                                <div className="cd-edit-row">
                                    <div className="cd-edit-field">
                                        <label>New Password</label>
                                        <div className="cd-edit-pass-wrap">
                                            <input
                                                className="cd-edit-input"
                                                type={showNewPass ? 'text' : 'password'}
                                                value={editPassword.new_password}
                                                onChange={e => setEditPassword(p => ({ ...p, new_password: e.target.value }))}
                                                placeholder="New password"
                                            />
                                            <button className="cd-pass-toggle" onClick={() => setShowNewPass(v => !v)}>
                                                {showNewPass ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="cd-edit-field">
                                        <label>Confirm New Password</label>
                                        <input
                                            className="cd-edit-input"
                                            type="password"
                                            value={editPassword.confirm}
                                            onChange={e => setEditPassword(p => ({ ...p, confirm: e.target.value }))}
                                            placeholder="Confirm password"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="cd-edit-modal-footer">
                                <button className="cd-edit-cancel-btn" onClick={() => setShowEditProfile(false)}>Cancel</button>
                                <button className="cd-edit-save-btn" onClick={handleSaveProfile} disabled={savingProfile}>
                                    {savingProfile ? 'Saving…' : <><FaCheck /> Save Changes</>}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

        </ClientProtected>
    );
};

export default ClientDashboard;
